import React, { useState } from "react";
import { Plus, X, Check, Trash2, Edit2 } from "lucide-react";
import { useAppStore } from "@/store";

interface Task {
  id: string;
  title: string;
  completed: boolean;
  pomodorosEstimated: number;
  pomodorosCompleted: number;
  createdAt: number;
  completedAt?: number;
}

interface TasksProps {}

const Tasks: React.FC<TasksProps> = () => {
  const MAX_TASKS = 5;

  // Get state and actions from store
  const { tasks, addTask, updateTask, deleteTask, incrementTaskPomodoro, isLoading } = useAppStore();

  // Filter tasks locally
  const activeTasks = tasks.filter((task) => !task.completed);
  const completedTasks = tasks.filter((task) => task.completed);
  
  const isMaxTasksReached = tasks.length >= MAX_TASKS;

  const [showAddTask, setShowAddTask] = useState<boolean>(false);
  const [newTaskTitle, setNewTaskTitle] = useState<string>("");
  const [newTaskPomodoros, setNewTaskPomodoros] = useState<number>(1);
  const [editingTask, setEditingTask] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState<string>("");

  const handleAddTask = (): void => {
    if (!newTaskTitle.trim()) return;

    try {
      addTask(newTaskTitle.trim(), newTaskPomodoros);
      setNewTaskTitle("");
      setNewTaskPomodoros(1);
      setShowAddTask(false);
    } catch (error: any) {
      console.error("Failed to add task:", error.message || error);
      alert(error.message || "Failed to add task");
    }
  };

  const handleDeleteTask = (id: string): void => {
    deleteTask(id);
  };

  const handleToggleTask = (id: string): void => {
    const task = tasks.find((t) => t.id === id);
    if (task) {
      updateTask(id, { completed: !task.completed });
    }
  };

  const startEditingTask = (task: Task): void => {
    setEditingTask(task.id);
    setEditTitle(task.title);
  };

  const saveTaskEdit = (id: string): void => {
    if (editTitle.trim()) {
      updateTask(id, { title: editTitle.trim() });
    }
    setEditingTask(null);
    setEditTitle("");
  };

  const cancelEdit = (): void => {
    setEditingTask(null);
    setEditTitle("");
  };

  const handleIncrementPomodoro = (id: string): void => {
    incrementTaskPomodoro(id);
  };

  const totalTasks = tasks.length;

  return (
 
      <div className="text-white  w-full ">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg">
            Tasks <span className="text-grey text-xs">(max: {MAX_TASKS})</span>
          </h3>
          <button
            onClick={() => setShowAddTask(true)}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-lg transition-colors"
            disabled={isLoading}
          >
            <Plus size={20} />
          </button>
        </div>

        {/* Loading indicator */}
        {isLoading && (
          <div className="text-center mb-4">
            <div className="text-white text-opacity-70 text-sm">Loading...</div>
          </div>
        )}

        {/* Add Task Form */}
        {showAddTask && (
          <div className="mb-4 p-4 bg-black bg-opacity-20 rounded-lg ">
            <input
              type="text"
              placeholder="What are you working on?"
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              className="w-full px-3 py-2 bg-white bg-opacity-20 text-white placeholder-white placeholder-opacity-70 rounded-lg border border-white border-opacity-30 focus:outline-none focus:border-opacity-50 mb-3"
              autoFocus
              disabled={isLoading}
            />
            <div className="flex items-center gap-2 mb-3">
              <label className="text-sm text-white text-opacity-80">
                Est Pomodoros:
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={newTaskPomodoros}
                onChange={(e) =>
                  setNewTaskPomodoros(parseInt(e.target.value) || 1)
                }
                className="w-16 px-2 py-1 bg-white bg-opacity-20 text-white rounded border border-white border-opacity-30 focus:outline-none focus:border-opacity-50"
                disabled={isLoading}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddTask}
                className="bg-white text-gray-800 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isMaxTasksReached || isLoading}
              >
                Add Task
              </button>
              <button
                onClick={() => {
                  setShowAddTask(false);
                  setNewTaskTitle("");
                  setNewTaskPomodoros(1);
                }}
                className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-opacity-30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Active Tasks */}
        {activeTasks.length > 0 && (
          <div className="mb-4">
            <div className="space-y-2">
              {activeTasks.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center gap-3 p-3 bg-white bg-opacity-10 rounded-lg"
                >
                  <button
                    onClick={() => handleToggleTask(task.id)}
                    className="w-5 h-5 rounded border-2 border-white border-opacity-60 flex items-center justify-center hover:border-opacity-80 transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isLoading}
                  >
                    {task.completed && (
                      <Check size={14} className="text-white" />
                    )}
                  </button>

                  <div className="flex-1 min-w-0">
                    {editingTask === task.id ? (
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="w-full px-2 py-1 bg-white bg-opacity-20 text-white rounded border border-white border-opacity-30 focus:outline-none focus:border-opacity-50"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveTaskEdit(task.id);
                          if (e.key === "Escape") cancelEdit();
                        }}
                        autoFocus
                        disabled={isLoading}
                      />
                    ) : (
                      <div>
                        <div className="text-white font-medium truncate">
                          {task.title}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="text-xs text-white text-opacity-70">
                            {task.pomodorosCompleted}/{task.pomodorosEstimated}
                          </div>
                          <div className="flex gap-1">
                            {Array.from(
                              { length: task.pomodorosEstimated },
                              (_, i) => (
                                <div
                                  key={i}
                                  className={`w-2 h-2 rounded-full ${
                                    i < task.pomodorosCompleted
                                      ? "bg-white"
                                      : "bg-white bg-opacity-30"
                                  }`}
                                />
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    {editingTask === task.id ? (
                      <>
                        <button
                          onClick={() => saveTaskEdit(task.id)}
                          className="p-1 text-white hover:bg-white hover:bg-opacity-20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={isLoading}
                        >
                          <Check size={16} />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="p-1 text-white hover:bg-white hover:bg-opacity-20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={isLoading}
                        >
                          <X size={16} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleIncrementPomodoro(task.id)}
                          className="px-2 py-1 text-xs bg-white bg-opacity-20 text-white rounded hover:bg-opacity-30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={
                            isLoading ||
                            task.pomodorosCompleted >= task.pomodorosEstimated
                          }
                        >
                          +1
                        </button>
                        <button
                          onClick={() => startEditingTask(task)}
                          className="p-1 text-white hover:bg-white hover:bg-opacity-20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={isLoading}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteTask(task.id)}
                          className="p-1 text-white hover:bg-white hover:bg-opacity-20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={isLoading}
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed Tasks */}
        {completedTasks.length > 0 && (
          <div className="border-t border-white border-opacity-20 pt-4">
            <h4 className="text-sm text-white text-opacity-70 mb-2">
              Completed ({completedTasks.length})
            </h4>
            <div className="space-y-2">
              {completedTasks.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center gap-3 p-3 bg-white bg-opacity-5 rounded-lg"
                >
                  <button
                    onClick={() => handleToggleTask(task.id)}
                    className="w-5 h-5 rounded border-2 border-white border-opacity-60 flex items-center justify-center bg-white bg-opacity-80 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isLoading}
                  >
                    <Check size={14} className="text-gray-800" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-opacity-70 font-medium truncate line-through">
                      {task.title}
                    </div>
                    <div className="text-xs text-white text-opacity-50">
                      {task.pomodorosCompleted}/{task.pomodorosEstimated}{" "}
                      pomodoros
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteTask(task.id)}
                    className="p-1 text-white text-opacity-70 hover:text-opacity-100 hover:bg-white hover:bg-opacity-20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isLoading}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {totalTasks === 0 && !showAddTask && (
          <div className="text-center py-8">
            <p className="text-white text-opacity-80 mb-4">
              Time to focus! What are you working on?
            </p>
            <button
              onClick={() => setShowAddTask(true)}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              Add Task
            </button>
          </div>
        )}
      </div>
  
  );
};

export default Tasks;
export type { TasksProps };